<template>
  <v-container
    fluid
    class="pa-0"
  >
    <v-layout
      row
      wrap
    >
      <v-flex>
        <v-card class="max-shadow">
          <v-card-title>
            <div class="title-2">
              Invoices
            </div>
            <v-spacer />
            <v-card-actions>
              <v-btn
                flat
                color="primary"
                @click="invoiceEditorDialog = true"
              >
                Create
              </v-btn>
            </v-card-actions>
          </v-card-title>
          <v-data-table
            id="invoicesTable"
            :headers="invoicesHeaders"
            :items="invoices"
            :loading="invoicesLoading"
            item-key="id"
            no-data-text="No invoices found"
            class="px-2 pb-2"
            disable-initial-sort
            hide-actions
          >
            <v-progress-linear
              slot="progress"
              color="primary"
              indeterminate
            />
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                class=" row-border"
                style="cursor: pointer;"
                @click="previewInvoice(props.item)"
              >
                <td style="width: 20px">
                  {{ props.index + 1 }}
                </td>
                <td class="text-xs-left">
                  {{ props.item.invoiceNumber }}
                </td>
                <td class="text-xs-left">
                  {{ formatDate(props.item.invoiceDate.toDate()) }}
                </td>
                <td class="text-xs-left">
                  {{ getStatus(props.item) }}
                </td>
                <td class="text-xs-left">
                  {{ formatDate(props.item.dueDate.toDate()) }}
                </td>
                <td class="text-xs-left">
                  {{ formatCurrency(props.item.total) }}
                </td>
                <td class="text-xs-left">
                  <v-menu
                    bottom
                    rigt
                    @click.native.stop
                  >
                    <v-btn
                      slot="activator"
                      icon
                    >
                      <v-icon>more_vert</v-icon>
                    </v-btn>

                    <v-list>
                      <v-list-tile
                        key="email"
                        @click.stop="sendInvoice(props.item.id)"
                      >
                        <v-list-tile-title>Email Invoice</v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile 
                        v-if="props.item.status !== 'PAID'"
                        key="mark"
                        @click="markInvoicePaid(props.item.id)"
                      >
                        <v-list-tile-title>Mark as Paid</v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout
      row
      justify-center
    >
      <v-dialog
        v-model="invoiceEditorDialog"
        scrollable
        persistent
        lazy
      >
        <v-card class="max-shadow">
          <v-card-title class="title">
            New Invoice
          </v-card-title>
          <v-divider />
          <new-invoice
            v-if="invoiceEditorDialog"
            ref="newInvoice"
            :tenant="tenant"
          />
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-checkbox
              v-model="emailInvoice"
              label="Email Invoice"
            />
            <v-btn
              color="primary"
              flat
              @click.native="invoiceEditorDialog = false"
            >
              Close
            </v-btn>
            <v-btn
              color="primary"
              flat
              @click.native="createInvoice"
            >
              Create
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="invoicePreviewDialog"
        scrollable 
        persistent
        lazy
        max-width="750"
      >
        <invoice-preview 
          :invoice="selectedInvoice"
          type="invoice"
          @close="invoicePreviewDialog = false"
        />
      </v-dialog>
    </v-layout>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.determinate ? 3000 : 0"
    >
      {{ snackbar.text }}
      <v-btn
        v-if="snackbar.determinate"
        flat
        color="accent"
        @click.native="snackbar.show = false"
      >
        Close
      </v-btn>
      <v-progress-circular
        v-else
        indeterminate
        color="white"
      />
    </v-snackbar>
  </v-container>
</template>

<script>
import { firestore, functions } from '@/firebase';
import moment from 'moment';
import InvoicePreview from '@/components/max-admin/tenants/InvoicePreview';

import NewInvoice from './NewInvoice';

export default {
  name: 'Invoices',
  components: {
    'new-invoice': NewInvoice,
    'invoice-preview': InvoicePreview
  },
  props: {
    tenantId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      tenant: {
        admin: {},
        billingSettings: {},
        invoiceSettings: {}
      },
      invoices: [],
      selectedInvoice: {},
      invoicesLoading: false,
      invoicesHeaders: [
        {
          text: '#',
          value: false,
          align: 'left',
          width: '10px'
        },
        {
          text: 'Invoice#',
          value: 'invoiceNumber',
          align: 'left'
        },
        {
          text: 'Date',
          value: 'invoiceDate',
          align: 'left'
        },
        {
          text: 'Status',
          value: 'status',
          align: 'left'
        },
        {
          text: 'Due Date',
          value: 'dueDate',
          align: 'left'
        },
        {
          text: 'Amount',
          value: 'total',
          align: 'left'
        },
        {
          text: 'Actions',
          value: false,
          align: 'left'
        }
      ],
      invoiceEditorDialog: false,
      invoicePreviewDialog: false,
      emailInvoice: false,
      snackbar: {
        show: false,
        text: '',
        determinate: true,
        color: 'primary'
      }
    };
  },
  created() {
    // fetch tenant details
    firestore
      .collection('tenants')
      .doc(this.tenantId)
      .get()
      .then(tenantSnapshot => {
        this.tenant = tenantSnapshot.data();
      });

    //fetch invoices
    this.fetchInvoices();
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    formatCurrency(val) {
      return val.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        currency: 'INR'
      });
    },

    getStatus(invoice) {
      return moment(invoice.dueDate.toDate()).isBefore(moment(), 'date')
        ? 'OVERDUE'
        : invoice.status;
    },

    createInvoice() {
      const invoice = this.$refs.newInvoice.validate();
      if (!invoice) return;

      firestore
        .collection('tenants')
        .doc(this.tenantId)
        .collection('invoices')
        .add(invoice)
        .then(() => {
          this.invoiceEditorDialog = false;
          this.fetchInvoices();
          if (this.emailInvoice) this.sendInvoice();
        })
        .catch(error => {
          console.log(error);
        });
    },

    sendInvoice(invoiceId) {
      // snackbar
      this.snackbar = {
        show: true,
        text: 'Sending invoice...',
        determinate: false,
        color: 'primary'
      };

      const emailInvoiceFnc = functions.httpsCallable('emailTenantInvoice');
      emailInvoiceFnc({
        tid: this.tenantId,
        invoiceId: invoiceId
      })
        .then(result => {
          if (result.data.status === 'SUCCESS') {
            this.snackbar = {
              show: true,
              text: 'Invoice email is sent',
              determinate: true,
              color: 'success'
            };
          }
        })
        .catch(error => {
          console.log(error);
          this.snackbar = {
            show: true,
            text: 'Could not send invoice email',
            determinate: true,
            color: 'error'
          };
        });
    },

    markInvoicePaid(invoiceId) {
      firestore
        .collection('tenants')
        .doc(this.tenantId)
        .collection('invoices')
        .doc(invoiceId)
        .update({ status: 'PAID' })
        .then(() => {
          this.fetchInvoices();
        })
        .catch(error => {
          console.log(error);
        });
    },

    fetchInvoices() {
      firestore
        .collection('tenants')
        .doc(this.tenantId)
        .collection('invoices')
        .get()
        .then(invoicesSnapshot => {
          this.invoices = [];
          invoicesSnapshot.forEach(invoiceSnap => {
            this.invoices.push(
              Object.assign({ id: invoiceSnap.id }, invoiceSnap.data())
            );
          });
        })
        .catch(error => {
          //TODO: handle errors wherever there are not handled
          console.log(error);
        });
    },

    previewInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.invoicePreviewDialog = true;
    }
  }
};
</script>
